import { AuditLog, AuditLogEventType, AuditLogSuggestions } from "../api/AuditLog";
import { IconNames } from "../common/icon/LucideIcon";
import { formatIsoDate } from "../common/datetime/DateTimeFormatter";
import { genericEntityConstants } from "../api/GenericConstants";

export const getAuditLogStringLabel = ({
  auditLog,
  hideUserInfo,
}: {
  auditLog: AuditLog | AuditLogSuggestions;
  hideUserInfo?: boolean;
}) => {
  const eventTypeInfo = eventIdToInfo(auditLog.eventId);
  return `${formatIsoDate(new Date(auditLog.timestamp)).datestr} ${
    formatIsoDate(new Date(auditLog.timestamp)).timestr
  }: ${eventTypeInfo.label}${auditLog.user && !hideUserInfo ? ` by ${auditLog.user.name}` : ""}`;
};

export const eventIdToInfo = (
  type: AuditLogEventType
): {
  icon?: IconNames;
  color: "primary" | "secondary" | "danger" | "warning" | "success" | "default";
  label: string;
} => {
  switch (type) {
    case "Login":
      return { icon: "user", color: "primary", label: "Login" };
    case "Logout":
      return { icon: "user-minus", color: "danger", label: "Logout" };
    case "ApiKeyCreate":
      return { icon: "key", color: "success", label: "API key created" };
    case "ApiKeySoftDelete":
      return { icon: "trash", color: "warning", label: "API key trashed" };
    case "ApiKeyRestore":
      return { icon: "rotate-ccw", color: "success", label: "API key restored" };
    case "ApiKeyDelete":
      return { icon: "trash-2", color: "danger", label: "API key deleted" };
    case "EntityCreate":
      return { icon: "plus", color: "success", label: "Content created" };
    case "EntityEdit":
      return { icon: "square-pen", color: "default", label: "Content edited" };
    case "EntitySoftDelete":
      return { icon: "trash", color: "warning", label: "Content trashed" };
    case "EntityRestore":
      return { icon: "copy", color: "success", label: "Content restored" };
    case "EntityDelete":
      return { icon: "trash-2", color: "danger", label: "Content deleted" };
    case "EntityVersionRestore":
      return { icon: "rotate-ccw", color: "success", label: "Version restored" };
    case "EntityShared":
      return { icon: "share", color: "primary", label: "Content shared" };
    case "EntityExported":
      return { icon: "download", color: "primary", label: "Content exported" };
    case "EntityFilesDownloaded":
      return { icon: "download", color: "primary", label: "Files downloaded" };
    case "EntityLocked":
      return { icon: "lock", color: "warning", label: "Content locked" };
    case "EntityLockedAndSigned":
      return { icon: "lock", color: "warning", label: "Content locked and signed" };
    case "EntityUnlocked":
      return { icon: "lock-open", color: "success", label: "Content unlocked" };
    case "EntityUnlockedAndUnsigned":
      return { icon: "lock-open", color: "success", label: "Content unlocked and signatures removed" };
    case "EntityFilesEdit":
      return { icon: "file-diff", color: "success", label: "Content files updated" };
    case "EntityFilesRemoved":
      return { icon: "file-diff", color: "danger", label: "Content files removed" };

    default:
      return { icon: "message-circle-question", color: "default", label: "-" };
  }
};

export const getAuditLogDefaultMessage = (auditLog: AuditLog | AuditLogSuggestions): string => {
  const entityInfo =
    auditLog.entityType && Object.hasOwn(genericEntityConstants, auditLog.entityType)
      ? genericEntityConstants[auditLog.entityType]
      : { entityConstants: { entitySingular: "unknown item", entityPlural: "unknown items", icon: "triangle-alert" } };

  var auditLogLength = 1;
  if (Object.hasOwnProperty.call(auditLog, "entityIntRecords")) {
    if ((auditLog as AuditLog).entityIntRecords) {
      auditLogLength = (auditLog as AuditLog).entityIntRecords?.length || 0;
    }
  } else if (Object.hasOwnProperty.call(auditLog, "entityLongRecords")) {
    if ((auditLog as AuditLog).entityLongRecords) {
      auditLogLength = (auditLog as AuditLog).entityLongRecords?.length || 0;
    }
  } else if (Object.hasOwnProperty.call(auditLog, "entityStringRecords")) {
    if ((auditLog as AuditLog).entityStringRecords) {
      auditLogLength = (auditLog as AuditLog).entityStringRecords?.length || 0;
    }
  }

  switch (auditLog.eventId) {
    case "Login":
      return "The user logged in.";
    case "Logout":
      return "The user logged out.";
    case "ApiKeyCreate":
      return "An API key was created.";
    case "ApiKeySoftDelete":
      return "An API key was moved to trash.";
    case "ApiKeyRestore":
      return "An API key was restored.";
    case "ApiKeyDelete":
      return "An API key was deleted.";
    case "EntityCreate":
      return auditLogLength > 1
        ? `${auditLogLength} new ${entityInfo?.entityConstants.entityPlural} were created.`
        : `A new ${entityInfo?.entityConstants.entitySingular} was created.`;
    case "EntityEdit":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were edited.`
        : `A ${entityInfo?.entityConstants.entitySingular} was edited.`;
    case "EntitySoftDelete":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were moved to trash.`
        : `A ${entityInfo?.entityConstants.entitySingular} was moved to trash.`;
    case "EntityRestore":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were restored.`
        : `A ${entityInfo?.entityConstants.entitySingular} was restored.`;
    case "EntityDelete":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were deleted permanently.`
        : `A ${entityInfo?.entityConstants.entitySingular} was deleted.`;
    case "EntityVersionRestore":
      return auditLogLength > 1
        ? `${auditLogLength} previous versions of ${entityInfo?.entityConstants.entityPlural} were restored.`
        : `A previous version of a ${entityInfo?.entityConstants.entitySingular} was restored.`;
    case "EntityShared":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were shared.`
        : `A ${entityInfo?.entityConstants.entitySingular} was shared.`;
    case "EntityExported":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were exported.`
        : `A ${entityInfo?.entityConstants.entitySingular} was exported.`;
    case "EntityFilesDownloaded":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were downloaded.`
        : `A ${entityInfo?.entityConstants.entitySingular} was downloaded.`;
    case "EntityLocked":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were locked.`
        : `A ${entityInfo?.entityConstants.entitySingular} was locked.`;
    case "EntityLockedAndSigned":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were locked and signed.`
        : `A ${entityInfo?.entityConstants.entitySingular} was locked and signed.`;
    case "EntityUnlocked":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were unlocked.`
        : `A ${entityInfo?.entityConstants.entitySingular} was unlocked.`;
    case "EntityUnlockedAndUnsigned":
      return auditLogLength > 1
        ? `${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were unlocked and all signatures were removed.`
        : `A ${entityInfo?.entityConstants.entitySingular} was unlocked and all signatures were removed.`;
    case "EntityFilesEdit":
      return auditLogLength > 1
        ? `The files of ${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were modified.`
        : `The files of a ${entityInfo?.entityConstants.entitySingular} were modified.`;
    case "EntityFilesRemoved":
      return auditLogLength > 1
        ? `The files of ${auditLogLength} ${entityInfo?.entityConstants.entityPlural} were removed.`
        : `The files of a ${entityInfo?.entityConstants.entitySingular} were removed.`;
    default:
      return "-";
  }
};
